import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { createPortal } from "react-dom";
import { useInsightsWs, useLiveTickerQuote } from "./Utilities/CoTraderUtils";
import { useLiveSentiment } from "./Utilities/useLiveSentiment";
import CoTraderInsightsPanel from "./CoTraderInsightsPanel";
import { TickerTapeSkeleton } from "./CoTraderSkeletons";
import CoTraderEmgptDiv from "./CoTraderEmgpt";
import CoTraderDemoLockedChatPanel from "./CoTraderDemoLockedChatPanel";
import Switch from "react-switch";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { FaBars } from "react-icons/fa";
import { Button, Dropdown } from "react-bootstrap";
import CoTraderFilterStocks from "./CoTraderFilterStocks";
import TradingViewChart from "../TradingView/TradingViewChart";
import { useStablePrependScroll } from "./useStablePrependScroll";
import { getUserId } from "../../utilities/authHelper";
import CoTraderCustomTickerTape from "../TradingView/CoTraderTickerTape";
import WorkspaceSettingsSection from "../Common/WorkspaceSettingsSection";
import WorkspacePresetPicker from "../Common/WorkspacePresetPicker";
import {
  EMPTY_STOCKS_CONFIG,
  fetchCoTraderStocksConfig,
  getInitialStockSelection,
  getStockDisplayCode,
  getStockSelectionForSymbol,
  getStockSymbolsFromOptions,
} from "./StocksOptions";
import {
  COTRADER_LANGUAGE_LIST,
  getStoredCoTraderLanguage,
} from "./Utilities/languages";

const WORKSPACE_THEME = {
  pageBg: "var(--app-page-bg, #eef3f8)",
  border: "var(--app-border, #E5EAF1)",
  panelBg: "var(--app-panel-bg, #FFFFFF)",
  panelAltBg: "var(--app-panel-alt-bg, #F8FAFC)",
  text: "var(--app-text, #0B1220)",
  muted: "var(--app-muted, #5B6472)",
};

const STOCKS_WORKSPACE_VERSION = "v3";
// Bumped when the default insights/assistant split changes, so saved panel
// sizes from the old split do not override it.
const STOCKS_ROW_LAYOUT_REVISION = "r2";
const STOCKS_WORKSPACE_PRODUCT = "co-trader-stocks";
const DEFAULT_STOCKS_WORKSPACE = {
  preset: "desk",
  shellLayout: "stacked",
  chartPosition: "top",
  gptPosition: "right",
};

const STOCKS_WORKSPACE_PRESETS = {
  desk: {
    label: "Desk",
    description: "Chart top, updates left, GPT right",
    config: DEFAULT_STOCKS_WORKSPACE,
  },
  research: {
    label: "Research",
    description: "Chart top, GPT left, compact sentiment above updates",
    config: {
      preset: "research",
      shellLayout: "stacked",
      chartPosition: "top",
      gptPosition: "left",
    },
  },
  split: {
    label: "Split View",
    description: "Chart and updates stack on one side, Stocks GPT spans the other",
    config: {
      preset: "split",
      shellLayout: "assistantColumn",
      chartPosition: "top",
      gptPosition: "right",
    },
  },
  updates: {
    label: "Updates",
    description: "Chart bottom, updates lead the page",
    config: {
      preset: "updates",
      shellLayout: "stacked",
      chartPosition: "bottom",
      gptPosition: "right",
    },
  },
  tape: {
    label: "Tape",
    description: "Chart hidden, catalyst tape first",
    config: {
      preset: "tape",
      shellLayout: "stacked",
      chartPosition: "collapsed",
      gptPosition: "right",
    },
  },
};

const SHELL_LAYOUT_OPTIONS = ["stacked", "assistantColumn"];
const CHART_POSITION_OPTIONS = ["top", "bottom", "collapsed"];
const GPT_POSITION_OPTIONS = ["right", "left", "collapsed"];
const STOCKS_UPDATES_TABS = [["updates", "Insights"]];

const getStocksWorkspaceStorageKey = (userId) =>
  `${STOCKS_WORKSPACE_PRODUCT}:workspace:${STOCKS_WORKSPACE_VERSION}:${
    userId || "anonymous"
  }`;

const getWorkspacePresetConfig = (presetKey) =>
  STOCKS_WORKSPACE_PRESETS[presetKey]?.config || DEFAULT_STOCKS_WORKSPACE;

const normalizeStocksWorkspace = (rawConfig = {}) => {
  const preset =
    rawConfig.preset && STOCKS_WORKSPACE_PRESETS[rawConfig.preset]
      ? rawConfig.preset
      : "custom";
  const presetBase =
    preset === "custom" ? DEFAULT_STOCKS_WORKSPACE : getWorkspacePresetConfig(preset);

  const shellLayout = SHELL_LAYOUT_OPTIONS.includes(rawConfig.shellLayout)
    ? rawConfig.shellLayout
    : presetBase.shellLayout;
  const chartPosition = CHART_POSITION_OPTIONS.includes(rawConfig.chartPosition)
    ? rawConfig.chartPosition
    : presetBase.chartPosition;
  const gptPosition = GPT_POSITION_OPTIONS.includes(rawConfig.gptPosition)
    ? rawConfig.gptPosition
    : presetBase.gptPosition;

  return {
    preset,
    shellLayout,
    chartPosition,
    gptPosition,
  };
};

const getInitialStocksWorkspace = (userId) => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(getStocksWorkspaceStorageKey(userId)) || "null",
    );

    if (parsed) {
      return normalizeStocksWorkspace(parsed);
    }
  } catch (error) {
    console.warn("Failed to load Stocks CoTrader workspace config", error);
  }

  return DEFAULT_STOCKS_WORKSPACE;
};

const getStockPromptSuggestions = (tickerLabel) => [
  `What changed for ${tickerLabel} today?`,
  `Summarize the latest ${tickerLabel} catalysts in 3 bullets.`,
  `Is sentiment improving or deteriorating for ${tickerLabel}?`,
  `What matters most for ${tickerLabel} into the close?`,
];

const CoTraderStocks = ({
  assetTypeLabelOverride = "Stock",
  chartIntervalOverride = null,
  chartPeriodOverride = null,
  defaultSymbolOverride = "",
  hideTopChrome = false,
  hideSelectionSummary = false,
  isDemoMode = false,
  selectedCategoryLabelOverride = "",
  selectedCountryLabelOverride = "",
  selectedSymbolOverride = "",
  selectedTickerLabelOverride = "",
  stockOptionsOverride = null,
  stockUniverseErrorOverride = "",
  stockUniverseLoadingOverride = null,
  toolbarPortalTarget = null,
}) => {
  const pageTitle = "Stocks CoTrader";
  const [stocksConfig, setStocksConfig] = useState(EMPTY_STOCKS_CONFIG);
  const [stockUniverseLoading, setStockUniverseLoading] = useState(true);
  const [stockUniverseError, setStockUniverseError] = useState("");
  const stockOptions = stockOptionsOverride || stocksConfig.options;
  const defaultStockSymbol = defaultSymbolOverride || stocksConfig.defaultSymbol;
  const effectiveStockUniverseLoading =
    typeof stockUniverseLoadingOverride === "boolean"
      ? stockUniverseLoadingOverride
      : stockUniverseLoading;
  const effectiveStockUniverseError =
    stockUniverseErrorOverride || stockUniverseError;
  const initialSelection = useMemo(
    () => getInitialStockSelection(stockOptions, defaultStockSymbol),
    [defaultStockSymbol, stockOptions],
  );
  const currentUserId = useMemo(() => getUserId() || "anonymous", []);
  const workspaceStorageKey = useMemo(
    () => getStocksWorkspaceStorageKey(currentUserId),
    [currentUserId],
  );
  const [updateSpeed, setUpdateSpeed] = useState(2);
  const [selectedSymbol, setSelectedSymbol] = useState(selectedSymbolOverride || "");
  const [selectedTickerLabel, setSelectedTickerLabel] = useState(
    selectedTickerLabelOverride || "",
  );
  const [selectedCountryLabel, setSelectedCountryLabel] = useState(
    selectedCountryLabelOverride || "",
  );
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState(
    selectedCategoryLabelOverride || "",
  );
  const showCountDownInsights = true;
  const selectedSymbolRef = useRef("");
  const [searchFlag, setSearchFlag] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [activeUpdatesTab, setActiveUpdatesTab] = useState("updates");
  const [showMenu, setShowMenu] = useState(false);
  const [settingsCollapseSignal, setSettingsCollapseSignal] = useState(0);
  const [language, setLanguage] = useState(() => getStoredCoTraderLanguage());
  const [workspaceConfig, setWorkspaceConfig] = useState(() =>
    getInitialStocksWorkspace(currentUserId),
  );
  const [languageKey, setLanguageKey] = useState(0);
  const [layoutResetKey, setLayoutResetKey] = useState(0);
  const [stockGptState, setStockGptState] = useState({
    hasConversation: false,
    isLoading: false,
  });
  const [pendingStockQuestion, setPendingStockQuestion] = useState(null);
  const [tabs, setTabs] = useState([]);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const stocksMode = useMemo(
    () =>
      stockGptState.hasConversation || stockGptState.isLoading ? "ask" : "monitor",
    [stockGptState.hasConversation, stockGptState.isLoading],
  );
  const GptPanelComponent = isDemoMode
    ? CoTraderDemoLockedChatPanel
    : CoTraderEmgptDiv;
  const showChart = workspaceConfig.chartPosition !== "collapsed";
  const showGpt = workspaceConfig.gptPosition !== "collapsed";
  const currentUpdatesTab = STOCKS_UPDATES_TABS[0][0];

  const layoutState = useMemo(
    () => ({
      verticalId: `cotrader-stocks-layout-vertical-${stocksMode}-${workspaceConfig.shellLayout}-${workspaceConfig.chartPosition}-${STOCKS_WORKSPACE_VERSION}`,
      horizontalId: `cotrader-stocks-layout-horizontal-${stocksMode}-${workspaceConfig.shellLayout}-${workspaceConfig.gptPosition}-${STOCKS_WORKSPACE_VERSION}-${STOCKS_ROW_LAYOUT_REVISION}`,
      shellHorizontalId: `cotrader-stocks-layout-shell-${stocksMode}-${workspaceConfig.shellLayout}-${workspaceConfig.gptPosition}-${STOCKS_WORKSPACE_VERSION}-${STOCKS_ROW_LAYOUT_REVISION}`,
      contentVerticalId: `cotrader-stocks-layout-content-${stocksMode}-${workspaceConfig.shellLayout}-${workspaceConfig.chartPosition}-${STOCKS_WORKSPACE_VERSION}`,
      workspacePanelSize: stocksMode === "ask" ? 80 : 72,
      workspacePanelMin: 28,
      chartPanelSize: stocksMode === "ask" ? 20 : 28,
      // Wide range: the chart/footer handle is always available.
      chartPanelMin: 12,
      chartPanelMax: 72,
      // Insights and the assistant split the row evenly by default.
      updatesPanelSize: 50,
      updatesPanelMin: 30,
      updatesPanelMax: 70,
      gptPanelSize: 50,
      gptPanelMin: 30,
      contentPanelSize: 50,
      contentPanelMin: 30,
    }),
    [
      stocksMode,
      workspaceConfig.chartPosition,
      workspaceConfig.gptPosition,
      workspaceConfig.shellLayout,
    ],
  );

  const stockCode = useMemo(
    () => getStockDisplayCode(selectedSymbol),
    [selectedSymbol],
  );
  const subscriptionTickerSymbols = useMemo(
    () =>
      getStockSymbolsFromOptions(stockOptions).map((symbol) =>
        getStockDisplayCode(symbol.value),
      ),
    [stockOptions],
  );
  const stockTickerLabels = useMemo(
    () =>
      getStockSymbolsFromOptions(stockOptions).reduce((labels, symbol) => {
        const rawSymbol = getStockDisplayCode(symbol.value);

        if (rawSymbol) {
          labels[rawSymbol] = symbol.label || rawSymbol;
        }

        return labels;
      }, {}),
    [stockOptions],
  );
  const stockTickerSymbols = useMemo(() => {
    const nextSymbols = [
      stockCode,
      ...subscriptionTickerSymbols,
    ].filter(Boolean);

    return Array.from(new Set(nextSymbols));
  }, [stockCode, subscriptionTickerSymbols]);
  const hasStockUniverse = stockTickerSymbols.length > 0;
  const { quote: liveStockQuote } = useLiveTickerQuote(stockCode, {
    enabled: Boolean(stockCode),
    isDemoMode,
  });

  const stockPromptSuggestions = useMemo(
    () => getStockPromptSuggestions(selectedTickerLabel || stockCode),
    [selectedTickerLabel, stockCode],
  );

  const languageList = COTRADER_LANGUAGE_LIST;

  const { insights, loading, sentiment, sentimentUpdatedAt } = useInsightsWs(
    selectedSymbol,
    updateSpeed,
    selectedSymbolRef,
    showCountDownInsights,
    language,
    1200,
    undefined,
    { isDemoMode },
  );

  const updatesScrollRef = useStablePrependScroll({
    itemCount: insights.length,
    enabled: currentUpdatesTab === "updates" && !loading,
    resetKey: `${selectedSymbol}:${language}`,
  });

  useEffect(() => {
    if (activeUpdatesTab !== currentUpdatesTab) {
      setActiveUpdatesTab(currentUpdatesTab);
    }
  }, [activeUpdatesTab, currentUpdatesTab]);

  const { value: liveSentimentValue, delta: sentimentDelta } = useLiveSentiment(
    sentiment,
    selectedSymbol,
  );

  useEffect(() => {
    if (stockOptionsOverride) {
      return () => {};
    }

    let isMounted = true;

    const loadStockUniverse = async () => {
      setStockUniverseLoading(true);
      setStockUniverseError("");

      try {
        const nextStocksConfig = await fetchCoTraderStocksConfig();
        if (!isMounted) return;
        setStocksConfig(nextStocksConfig);
      } catch (error) {
        if (error?.status === 401) {
          window.location.href = "/logout";
          return;
        }
        if (!isMounted) return;
        setStocksConfig(EMPTY_STOCKS_CONFIG);
        setStockUniverseError(error?.message || "Stock universe is unavailable.");
      } finally {
        if (isMounted) {
          setStockUniverseLoading(false);
        }
      }
    };

    void loadStockUniverse();

    return () => {
      isMounted = false;
    };
  }, [stockOptionsOverride]);

  useEffect(() => {
    if (selectedSymbolOverride && selectedSymbolOverride !== selectedSymbol) {
      setSelectedSymbol(selectedSymbolOverride);
    }
  }, [selectedSymbol, selectedSymbolOverride]);

  useEffect(() => {
    if (
      typeof selectedTickerLabelOverride === "string" &&
      selectedTickerLabelOverride !== selectedTickerLabel
    ) {
      setSelectedTickerLabel(selectedTickerLabelOverride);
    }
  }, [selectedTickerLabel, selectedTickerLabelOverride]);

  useEffect(() => {
    if (
      typeof selectedCountryLabelOverride === "string" &&
      selectedCountryLabelOverride !== selectedCountryLabel
    ) {
      setSelectedCountryLabel(selectedCountryLabelOverride);
    }
  }, [selectedCountryLabel, selectedCountryLabelOverride]);

  useEffect(() => {
    if (
      typeof selectedCategoryLabelOverride === "string" &&
      selectedCategoryLabelOverride !== selectedCategoryLabel
    ) {
      setSelectedCategoryLabel(selectedCategoryLabelOverride);
    }
  }, [selectedCategoryLabel, selectedCategoryLabelOverride]);

  useEffect(() => {
    if (!initialSelection?.symbol) {
      setSelectedSymbol("");
      setSelectedTickerLabel("");
      setSelectedCountryLabel("");
      setSelectedCategoryLabel("");
      return;
    }

    if (getStockSelectionForSymbol(stockOptions, selectedSymbol)) {
      return;
    }

    setSelectedSymbol(initialSelection.symbol.value);
    setSelectedTickerLabel(initialSelection.symbol.label);
    setSelectedCountryLabel(initialSelection.country.label);
    setSelectedCategoryLabel(initialSelection.category.label);
  }, [initialSelection, selectedSymbol, stockOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(workspaceStorageKey, JSON.stringify(workspaceConfig));
  }, [workspaceConfig, workspaceStorageKey]);

  useEffect(() => {
    if (localStorage.getItem("user_language") !== language) {
      localStorage.setItem("user_language", language);
    }
  }, [language]);

  const applyWorkspacePreset = (presetKey) => {
    setWorkspaceConfig(getWorkspacePresetConfig(presetKey));
    setLayoutResetKey((prev) => prev + 1);
  };

  const updateWorkspaceConfig = (nextConfig) => {
    setWorkspaceConfig((previousConfig) =>
      normalizeStocksWorkspace({
        ...previousConfig,
        ...nextConfig,
        preset: "custom",
      }),
    );
    setLayoutResetKey((prev) => prev + 1);
  };

  const resetLayout = () => {
    Object.keys(localStorage)
      .filter(
        (key) =>
          key.startsWith("react-resizable-panels:cotrader-stocks-layout-") ||
          key === workspaceStorageKey,
      )
      .forEach((key) => localStorage.removeItem(key));
    setWorkspaceConfig(DEFAULT_STOCKS_WORKSPACE);
    setActiveUpdatesTab("updates");
    setLayoutResetKey((prev) => prev + 1);
  };

  const handleAskFromUpdate = (question) => {
    if (typeof question !== "string" || !question.trim()) {
      return;
    }

    if (workspaceConfig.gptPosition === "collapsed") {
      setWorkspaceConfig((previousConfig) =>
        normalizeStocksWorkspace({
          ...previousConfig,
          gptPosition: "right",
          preset: "custom",
        }),
      );
      setLayoutResetKey((prev) => prev + 1);
    }

    setPendingStockQuestion({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text: question.trim(),
    });
  };

  const handleSelectionContextChange = ({
    country,
    category,
    symbol,
    updateSpeed: nextUpdateSpeed,
  }) => {
    if (country?.label) {
      setSelectedCountryLabel(country.label);
    }
    if (category?.label) {
      setSelectedCategoryLabel(category.label);
    }
    if (symbol?.label) {
      setSelectedTickerLabel(symbol.label);
    }
    if (symbol?.value) {
      setSelectedSymbol(symbol.value);
    }
    if (nextUpdateSpeed?.value) {
      setUpdateSpeed(nextUpdateSpeed.value);
    }
  };

  const liveStockDisplayPrice = useMemo(() => {
    const nextValue = Number(
      liveStockQuote?.current_price ??
        liveStockQuote?.spot_midpoint ??
        liveStockQuote?.price ??
        liveStockQuote?.last,
    );

    return Number.isFinite(nextValue) ? nextValue : null;
  }, [liveStockQuote]);
  const currentWorkspaceLabel =
    workspaceConfig.preset === "custom"
      ? "Custom"
      : STOCKS_WORKSPACE_PRESETS[workspaceConfig.preset]?.label || "Desk";
  const hiddenModules = [
    showChart ? null : { label: "Show Chart", onClick: () => updateWorkspaceConfig({ chartPosition: "top" }) },
    showGpt ? null : { label: "Show GPT", onClick: () => updateWorkspaceConfig({ gptPosition: "right" }) },
  ].filter(Boolean);
  const workspaceActionButtons = hiddenModules.map((module) => (
    <button
      type="button"
      key={module.label}
      className="btn btn-sm rounded-pill"
      style={{
        background: "var(--app-panel-bg, #FFFFFF)",
        border: `1px solid ${WORKSPACE_THEME.border}`,
        color: WORKSPACE_THEME.text,
        fontWeight: 600,
      }}
      onClick={module.onClick}
    >
      {module.label}
    </button>
  ));
  const shouldPortalToolbar = Boolean(toolbarPortalTarget);
  const showHeaderSummary = !hideSelectionSummary;
  const showInlineHeaderToolbar = !shouldPortalToolbar;
  const showInlineHiddenModules = !shouldPortalToolbar && hiddenModules.length > 0;
  const showHeaderRow =
    showHeaderSummary || showInlineHiddenModules || showInlineHeaderToolbar;
  const workspaceToolbar = (
    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
      {shouldPortalToolbar ? workspaceActionButtons : null}
      <WorkspacePresetPicker
        presets={STOCKS_WORKSPACE_PRESETS}
        activePreset={workspaceConfig.preset}
        label={currentWorkspaceLabel}
        onSelect={applyWorkspacePreset}
        onMoreOptions={() => setShowMenu(true)}
      />
      <div className="d-flex align-items-center gap-2">
        <div
          style={{
            color: WORKSPACE_THEME.text,
            fontWeight: 700,
            fontSize: shouldPortalToolbar ? "0.86rem" : "0.95rem",
            minWidth: shouldPortalToolbar ? "70px" : "84px",
            textAlign: "right",
          }}
        >
          {liveStockDisplayPrice != null ? liveStockDisplayPrice.toFixed(4) : "--"}
        </div>

        <div className="position-relative">
          <Button
            variant="outline-secondary"
            onClick={() => setShowMenu((prev) => !prev)}
            className="p-1 d-flex align-items-center"
            ref={menuButtonRef}
            aria-haspopup="true"
            aria-expanded={showMenu}
          >
            <FaBars size={shouldPortalToolbar ? 18 : 20} />
          </Button>

          {showMenu && (
            <div
              ref={menuRef}
              className="bg-white shadow-lg rounded-3 p-3 position-absolute"
              style={{
                right: 0,
                top: "40px",
                zIndex: 1050,
                width: "min(340px, calc(100vw - 24px))",
                maxHeight: "calc(100vh - 96px)",
                border: `1px solid ${WORKSPACE_THEME.border}`,
                display: "flex",
                overflow: "visible",
              }}
              role="menu"
            >
              <div
                className="d-flex flex-column gap-3"
                style={{ flex: 1, minHeight: 0 }}
              >
                {[
                  {
                    label: "Show Suggestions",
                    icon: "fa-lightbulb",
                    checked: showSuggestions,
                    onChange: () => setShowSuggestions((prev) => !prev),
                  },
                ].map(({ label, icon, checked, onChange }) => (
                  <div
                    className="d-flex justify-content-between align-items-center"
                    key={label}
                  >
                    <span className="d-flex align-items-center text-secondary">
                      <i className={`fa ${icon} me-2`} />
                      {label}
                    </span>
                    <Switch
                      onChange={onChange}
                      checked={checked}
                      height={20}
                      width={40}
                    />
                  </div>
                ))}

                <div
                  className="d-flex flex-column gap-3 pe-1"
                  style={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                  }}
                >
                  <WorkspaceSettingsSection
                    title="Workspace Presets"
                    collapseSignal={settingsCollapseSignal}
                  >
                    <div className="d-grid gap-2">
                      {Object.entries(STOCKS_WORKSPACE_PRESETS).map(
                        ([presetKey, presetValue]) => {
                          const isActive =
                            workspaceConfig.preset === presetKey &&
                            workspaceConfig.preset !== "custom";

                          return (
                            <button
                              key={presetKey}
                              type="button"
                              className="btn btn-sm text-start rounded-3"
                              style={{
                                background: isActive
                                  ? "rgba(13, 110, 253, 0.08)"
                                  : "var(--app-panel-bg, #FFFFFF)",
                                border: isActive
                                  ? "1px solid rgba(13, 110, 253, 0.28)"
                                  : `1px solid ${WORKSPACE_THEME.border}`,
                                color: WORKSPACE_THEME.text,
                                padding: "0.65rem 0.8rem",
                              }}
                              onClick={() => applyWorkspacePreset(presetKey)}
                            >
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: "0.86rem",
                                }}
                              >
                                {presetValue.label}
                              </div>
                              <div
                                style={{
                                  color: WORKSPACE_THEME.muted,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {presetValue.description}
                              </div>
                            </button>
                          );
                        }
                      )}
                    </div>
                  </WorkspaceSettingsSection>

                  <WorkspaceSettingsSection
                    title="Module Positions"
                    collapseSignal={settingsCollapseSignal}
                  >
                    {[
                      {
                        label: "Workspace Shape",
                        value: workspaceConfig.shellLayout,
                        options: [
                          ["stacked", "Stacked"],
                          ["assistantColumn", "Split"],
                        ],
                        onChange: (shellLayout) =>
                          updateWorkspaceConfig({ shellLayout }),
                      },
                      {
                        label: "Chart",
                        value: workspaceConfig.chartPosition,
                        options: [
                          ["top", "Top"],
                          ["bottom", "Bottom"],
                          ["collapsed", "Hidden"],
                        ],
                        onChange: (chartPosition) =>
                          updateWorkspaceConfig({ chartPosition }),
                      },
                      {
                        label: "Stocks GPT",
                        value: workspaceConfig.gptPosition,
                        options: [
                          ["right", "Right"],
                          ["left", "Left"],
                          ["collapsed", "Hidden"],
                        ],
                        onChange: (gptPosition) =>
                          updateWorkspaceConfig({ gptPosition }),
                      },
                    ].map((group) => (
                      <div
                        key={group.label}
                        className="d-flex align-items-center justify-content-between gap-2 mb-2"
                      >
                        <span
                          style={{
                            color: WORKSPACE_THEME.text,
                            fontSize: "0.84rem",
                            fontWeight: 600,
                            minWidth: "92px",
                          }}
                        >
                          {group.label}
                        </span>
                        <div className="d-flex gap-1 flex-wrap justify-content-end">
                          {group.options.map(([optionValue, optionLabel]) => {
                            const isActive = group.value === optionValue;

                            return (
                              <button
                                key={optionValue}
                                type="button"
                                className="btn btn-sm rounded-pill"
                                style={{
                                  background: isActive
                                    ? "#0D6EFD"
                                    : "var(--app-panel-bg, #FFFFFF)",
                                  color: isActive
                                    ? "#FFFFFF"
                                    : WORKSPACE_THEME.text,
                                  border: isActive
                                    ? "1px solid #0D6EFD"
                                    : `1px solid ${WORKSPACE_THEME.border}`,
                                  fontWeight: 600,
                                }}
                                onClick={() => group.onChange(optionValue)}
                              >
                                {optionLabel}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </WorkspaceSettingsSection>
                </div>

                <Button variant="outline-danger" onClick={resetLayout}>
                  Reset Layout
                </Button>

                <div className="d-flex align-items-center w-100">
                  <div className="me-2 text-secondary">Language:</div>
                  <div className="flex-grow-1">
                    <Dropdown
                      onToggle={(nextShow) => {
                        if (nextShow) {
                          setSettingsCollapseSignal((current) => current + 1);
                        }
                      }}
                    >
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        id="language-dropdown"
                        className="w-100"
                      >
                        {language}
                      </Dropdown.Toggle>

                      <Dropdown.Menu
                        className="w-100"
                        style={{
                          maxHeight: "min(280px, calc(100vh - 120px))",
                          overflowY: "auto",
                        }}
                      >
                        {languageList.map((lang) => (
                          <Dropdown.Item
                            key={lang}
                            onClick={() => {
                              localStorage.setItem("user_language", lang);
                              setLanguage(lang);
                              setLanguageKey((prev) => prev + 1);
                            }}
                            active={language === lang}
                          >
                            {lang}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const horizontalResizeHandle = (
    <PanelResizeHandle
      style={{
        width: "6px",
        backgroundColor: "transparent",
        cursor: "col-resize",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "4px",
          height: "46px",
          borderRadius: "999px",
          background: "var(--app-grip, #d9e0e8)",
        }}
      />
    </PanelResizeHandle>
  );

  // Always available: drag to trade chart height for the insights/chat row.
  // Its 16px strip replaces the gap between the two, so spacing is unchanged.
  const verticalResizeHandle = (
    <PanelResizeHandle className="ct-resize-handle" title="Drag to resize the chart">
      <span className="ct-resize-grip" aria-hidden="true" />
    </PanelResizeHandle>
  );

  const renderStocksChartSurface = (paddingClass) => (
    <div className={`${paddingClass} h-100`} style={{ minHeight: 0 }}>
      <div
        className={`ct-panel ct-panel--chart ${
          stocksMode === "monitor" ? "is-focused" : ""
        }`}
      >
        <TradingViewChart
          defaultInterval={chartIntervalOverride ?? "1m"}
          defaultPeriod={chartPeriodOverride ?? "5d"}
          intervalOverride={chartIntervalOverride}
          periodOverride={chartPeriodOverride}
          hideHeader={hideSelectionSummary}
          symbol={stockCode}
          titleOverride={selectedTickerLabel || undefined}
          liveQuote={liveStockQuote}
        />
      </div>
    </div>
  );

  const renderStocksGptSurface = (paddingClass) => (
    <div className={`${paddingClass} h-100`}>
      <div className={`ct-panel ${stocksMode === "ask" ? "is-focused" : ""}`}>
        <div className={isDemoMode ? "ct-panel-scroll" : "h-100"}>
          <GptPanelComponent
            selectedSymbol={selectedSymbol}
            selectedTickerLabel={selectedTickerLabel}
            searchFlag={searchFlag}
            setSearchFlag={setSearchFlag}
            showSuggestions={showSuggestions}
            setShowSuggestions={setShowSuggestions}
            tabs={tabs}
            setTabs={setTabs}
            assistantLabel="Stocks GPT"
            onConversationStateChange={setStockGptState}
            externalQuestionRequest={pendingStockQuestion}
            suggestionOverrides={stockPromptSuggestions}
          />
        </div>
      </div>
    </div>
  );

  const renderStocksUpdatesSurface = (paddingClass) => (
    <div className={`${paddingClass} h-100`}>
      <CoTraderInsightsPanel
        tabs={STOCKS_UPDATES_TABS}
        activeTab={currentUpdatesTab}
        onTabChange={setActiveUpdatesTab}
        sentimentValue={liveSentimentValue}
        sentimentDelta={sentimentDelta}
        sentimentUpdatedAt={sentimentUpdatedAt}
        loading={loading}
        updates={insights}
        scrollRef={updatesScrollRef}
        onAskQuestion={handleAskFromUpdate}
        isFocused={stocksMode === "monitor"}
      />
    </div>
  );

  // Insights beside the assistant (either side), or alone when it is hidden.
  const renderMarketRow = (verticalPadding) => {
    if (!showGpt) {
      return renderStocksUpdatesSurface(`px-3 ${verticalPadding}`);
    }

    const isGptLeft = workspaceConfig.gptPosition === "left";
    const gptPanel = (
      <Panel
        defaultSize={layoutState.gptPanelSize}
        minSize={layoutState.gptPanelMin}
      >
        {renderStocksGptSurface(
          `${isGptLeft ? "ps-3 pe-2" : "ps-2 pe-3"} ${verticalPadding}`
        )}
      </Panel>
    );

    return (
      <PanelGroup
        key={`cotrader-stocks-horizontal-${workspaceConfig.gptPosition}-${layoutResetKey}`}
        direction="horizontal"
        autoSaveId={layoutState.horizontalId}
      >
        {isGptLeft ? (
          <>
            {gptPanel}
            {horizontalResizeHandle}
          </>
        ) : null}
        <Panel
          defaultSize={layoutState.updatesPanelSize}
          minSize={layoutState.updatesPanelMin}
          maxSize={layoutState.updatesPanelMax}
        >
          {renderStocksUpdatesSurface(
            `${isGptLeft ? "ps-2 pe-3" : "ps-3 pe-2"} ${verticalPadding}`
          )}
        </Panel>
        {!isGptLeft ? (
          <>
            {horizontalResizeHandle}
            {gptPanel}
          </>
        ) : null}
      </PanelGroup>
    );
  };

  const renderStackedWorkspace = () => {
    if (!showChart) {
      return renderMarketRow("pt-2 pb-3");
    }

    const chartPanel = (
      <Panel
        defaultSize={layoutState.chartPanelSize}
        minSize={layoutState.chartPanelMin}
        maxSize={layoutState.chartPanelMax}
      >
        {renderStocksChartSurface(
          workspaceConfig.chartPosition === "bottom" ? "px-3 pt-0 pb-3" : "px-3 pt-2 pb-0"
        )}
      </Panel>
    );
    const marketPanel = (
      <Panel
        defaultSize={layoutState.workspacePanelSize}
        minSize={layoutState.workspacePanelMin}
      >
        {renderMarketRow(
          workspaceConfig.chartPosition === "bottom" ? "pt-2 pb-0" : "pt-0 pb-3"
        )}
      </Panel>
    );

    return (
      <PanelGroup
        direction="vertical"
        key={`cotrader-stocks-vertical-${workspaceConfig.chartPosition}-${layoutResetKey}`}
        autoSaveId={layoutState.verticalId}
        className="h-100"
      >
        {workspaceConfig.chartPosition === "bottom" ? marketPanel : chartPanel}
        {verticalResizeHandle}
        {workspaceConfig.chartPosition === "bottom" ? chartPanel : marketPanel}
      </PanelGroup>
    );
  };

  // Chart and insights stacked in one column, with the assistant spanning
  // the full height beside them.
  const renderSplitContentColumn = (sidePadding) => {
    if (!showChart) {
      return renderStocksUpdatesSurface(`${sidePadding} pt-2 pb-3`);
    }

    const isChartBottom = workspaceConfig.chartPosition === "bottom";
    const chartPanel = (
      <Panel
        defaultSize={layoutState.chartPanelSize}
        minSize={layoutState.chartPanelMin}
        maxSize={layoutState.chartPanelMax}
      >
        {renderStocksChartSurface(`${sidePadding} ${isChartBottom ? "pt-0 pb-3" : "pt-2 pb-0"}`)}
      </Panel>
    );
    const updatesPanel = (
      <Panel
        defaultSize={layoutState.workspacePanelSize}
        minSize={layoutState.workspacePanelMin}
      >
        {renderStocksUpdatesSurface(`${sidePadding} ${isChartBottom ? "pt-2 pb-0" : "pt-0 pb-3"}`)}
      </Panel>
    );

    return (
      <PanelGroup
        direction="vertical"
        autoSaveId={layoutState.contentVerticalId}
        key={`cotrader-stocks-content-${workspaceConfig.chartPosition}-${layoutResetKey}`}
        className="h-100"
      >
        {isChartBottom ? updatesPanel : chartPanel}
        {verticalResizeHandle}
        {isChartBottom ? chartPanel : updatesPanel}
      </PanelGroup>
    );
  };

  const renderAssistantColumnWorkspace = () => {
    if (!showGpt) {
      return renderSplitContentColumn("px-3");
    }

    const isGptLeft = workspaceConfig.gptPosition === "left";
    const gptPanel = (
      <Panel
        defaultSize={layoutState.gptPanelSize}
        minSize={layoutState.gptPanelMin}
      >
        {renderStocksGptSurface(`${isGptLeft ? "ps-3 pe-2" : "ps-2 pe-3"} pt-2 pb-3`)}
      </Panel>
    );

    return (
      <PanelGroup
        direction="horizontal"
        autoSaveId={layoutState.shellHorizontalId}
        key={`cotrader-stocks-shell-${workspaceConfig.gptPosition}-${layoutResetKey}`}
        className="h-100"
      >
        {isGptLeft ? (
          <>
            {gptPanel}
            {horizontalResizeHandle}
          </>
        ) : null}
        <Panel
          defaultSize={layoutState.contentPanelSize}
          minSize={layoutState.contentPanelMin}
        >
          {renderSplitContentColumn(isGptLeft ? "ps-2 pe-3" : "ps-3 pe-2")}
        </Panel>
        {!isGptLeft ? (
          <>
            {horizontalResizeHandle}
            {gptPanel}
          </>
        ) : null}
      </PanelGroup>
    );
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle} | EMAlpha</title>
      </Helmet>
      <div
        className="ct-page w-100 h-100 d-flex flex-column"
        style={{ minHeight: 0 }}
      >
        {!hideTopChrome && (
          <div className="px-3 pt-3 pb-2">
            <div className="ct-chrome px-3 py-2">
              {effectiveStockUniverseLoading ? (
                <TickerTapeSkeleton />
              ) : effectiveStockUniverseError || !hasStockUniverse ? (
                <div className="ct-chrome-status is-error">
                  {effectiveStockUniverseError || "Stock picker unavailable."}
                </div>
              ) : (
                <CoTraderCustomTickerTape
                  symbols={stockTickerSymbols}
                  symbolLabels={stockTickerLabels}
                />
              )}
            </div>
          </div>
        )}
        {!hideTopChrome && (
          <div className="px-3 pb-2">
            <div className="ct-chrome px-3 py-3">
              <CoTraderFilterStocks
                stockOptions={stockOptions}
                defaultSymbol={defaultStockSymbol}
                setSelectedSymbol={setSelectedSymbol}
                setUpdateSpeed={setUpdateSpeed}
                setSelectedTickerLabel={setSelectedTickerLabel}
                onSelectionContextChange={handleSelectionContextChange}
                isDisabled={
                  effectiveStockUniverseLoading || Boolean(effectiveStockUniverseError)
                }
              />
            </div>
          </div>
        )}

        <div
          className="flex-grow-1 d-flex flex-column"
          key={languageKey}
          style={{ minHeight: 0 }}
        >
          <div className="h-100 d-flex flex-column" style={{ minHeight: 0 }}>
            {showHeaderRow ? (
              <div
                className={`px-3 pt-1 d-flex align-items-center ${
                  showHeaderSummary || showInlineHiddenModules
                    ? "justify-content-between"
                    : "justify-content-end"
                }`}
              >
                {showHeaderSummary || showInlineHiddenModules ? (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {showHeaderSummary ? (
                      <>
                        <span
                          style={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: WORKSPACE_THEME.muted,
                          }}
                        >
                          {assetTypeLabelOverride}
                        </span>
                        <span
                          style={{
                            color: WORKSPACE_THEME.text,
                            fontWeight: 700,
                            fontSize: "0.95rem",
                          }}
                        >
                          {selectedTickerLabel}
                        </span>
                        <span
                          style={{
                            color: WORKSPACE_THEME.muted,
                            fontWeight: 500,
                            fontSize: "0.9rem",
                          }}
                        >
                          {selectedCountryLabel} {selectedCategoryLabel}
                        </span>
                      </>
                    ) : null}
                    {showInlineHiddenModules ? workspaceActionButtons : null}
                  </div>
                ) : null}
                {showInlineHeaderToolbar ? workspaceToolbar : null}
              </div>
            ) : null}
            {shouldPortalToolbar
              ? createPortal(workspaceToolbar, toolbarPortalTarget)
              : null}

            <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
              {workspaceConfig.shellLayout === "assistantColumn"
                ? renderAssistantColumnWorkspace()
                : renderStackedWorkspace()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoTraderStocks;
